import React from 'react';
import { useAuthSession } from '../../features/auth/shared/hooks/auth-session-context';
import { authTokenService } from '../../lib/authTokenService';
import { Button } from '../button';
import { RefreshCw, Shield, ShieldOff } from 'lucide-react';

interface AuthStatusProps {
    showRefreshButton?: boolean;
    className?: string;
}

export const AuthStatus: React.FC<AuthStatusProps> = ({
    showRefreshButton = true,
    className = ''
}) => {
    const { isLoggedIn: isAuthenticated, isInitialized, clearSession } = useAuthSession();
    const isLoading = !isInitialized;

    const handleRefreshToken = async () => {
        try {
            await authTokenService.refreshToken();
        } catch (error) {
            if (process.env.NODE_ENV === 'development') {
                console.error('Manual token refresh failed:', error);
            }
        }
    };

    if (isLoading) {
        return (
            <div className={`flex items-center gap-2 text-gray-500 dark:text-gray-400 ${className}`}>
                <RefreshCw className="h-4 w-4 animate-spin" />
                <span className="text-sm">Checking auth...</span>
            </div>
        );
    }

    return (
        <div className={`flex items-center gap-2 ${className}`}>
            {isAuthenticated ? (
                <>
                    <Shield className="h-4 w-4 text-green-500 dark:text-green-400" />
                    <span className="text-sm text-green-600 dark:text-green-400">Authenticated</span>
                    {showRefreshButton && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleRefreshToken}
                            className="ml-2"
                        >
                            <RefreshCw className="h-3 w-3 mr-1" />
                            Refresh Token
                        </Button>
                    )}
                </>
            ) : (
                <>
                    <ShieldOff className="h-4 w-4 text-red-500 dark:text-red-400" />
                    <span className="text-sm text-red-600 dark:text-red-400">Not Authenticated</span>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                            clearSession();
                            authTokenService.redirectToLogin();
                        }}
                        className="ml-2"
                    >
                        Login
                    </Button>
                </>
            )}
        </div>
    );
}; 
